import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { AccountingEntry, Document } from '../../types';
import apiService from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatUtils';
import CustomDialog from '../common/CustomDialog';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
}

const BackButton = styled(OutlineButton)`
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  p {
    margin: 0;
    color: var(--text-color);
  }
`;

const AmountText = styled.span<{ type: string }>`
  font-weight: 700;
  font-size: 1.5rem;
  color: ${props => props.type === 'Income' ? 'var(--success-color)' : 
    props.type === 'Expense' ? 'var(--danger-color)' : 'var(--text-color)'};
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Attachment = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: var(--light-gray);
  border-radius: var(--border-radius);
  
  .file-icon {
    background-color: var(--secondary-color);
    color: white;
    padding: 0.5rem;
    border-radius: var(--border-radius);
    font-size: 0.75rem;
  }
  
  .file-info {
    flex: 1;
    
    .file-name {
      font-weight: 500;
    }
    
    .file-meta {
      font-size: 0.75rem;
      color: var(--dark-gray);
    }
  }
`;

const NoAttachments = styled.p`
  color: var(--dark-gray);
  font-style: italic;
`;

const ApprovalStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${props => props.color || 'var(--medium-gray)'};
  }
`;

const SmallOutlineButton = styled(OutlineButton)`
  padding: 4px 8px;
  font-size: 0.85rem;
`;

const FinanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entry, setEntry] = useState<AccountingEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approvedByUser, setApprovedByUser] = useState<User | null>(null);
  const [createdByUser, setCreatedByUser] = useState<User | null>(null);
  
  // Dialog states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');
  
  // Check if current user can manage finances
  const canManageFinances = user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant';

  useEffect(() => {
    const loadEntry = async () => {
      if (id) {
        try {
          setLoading(true);
          const response = await apiService.getAccountingEntry(id);
          if (response.success && response.data) {
            setEntry(response.data);
            
            // Fetch user names for approvedBy and createdBy
            if (response.data.approvedBy) {
              try {
                const approvedByResponse = await apiService.getUser(response.data.approvedBy);
                if (approvedByResponse.success && approvedByResponse.data) {
                  setApprovedByUser(approvedByResponse.data);
                }
              } catch (error) {
                console.error('Error loading approved by user:', error);
              }
            }
            
            if (response.data.createdBy) {
              try {
                const createdByResponse = await apiService.getUser(response.data.createdBy);
                if (createdByResponse.success && createdByResponse.data) {
                  setCreatedByUser(createdByResponse.data);
                }
              } catch (error) {
                console.error('Error loading created by user:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error loading entry:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadEntry();
  }, [id]);

  if (loading) {
    return <div>Loading entry details...</div>;
  }

  if (!entry) {
    return <div>Entry not found</div>;
  }

  // Use the imported formatCurrency and formatDate functions

  const handleDelete = () => {
    setDialogTitle('Confirm Delete');
    setDialogMessage('Are you sure you want to delete this entry? This action cannot be undone.');
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!entry) return;
    
    try {
      const entryId = entry._id || entry.id;
      if (!entryId) {
        setDialogTitle('Error');
        setDialogMessage('Entry ID not found');
        setShowErrorDialog(true);
        return;
      }
      
      const response = await apiService.deleteAccountingEntry(entryId);
      
      if (response.success) {
        setDialogTitle('Success');
        setDialogMessage('Entry deleted successfully!');
        setShowSuccessDialog(true);
        setTimeout(() => {
          navigate('/office');
        }, 2000);
      } else {
        setDialogTitle('Error');
        setDialogMessage('Failed to delete entry: ' + (response.message || 'Unknown error'));
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      setDialogTitle('Error');
      setDialogMessage('Failed to delete entry. Please try again.');
      setShowErrorDialog(true);
    }
  };

  const handleConfirmDialog = async () => {
    // This will be called for the confirm dialog
    // The actual action (approve or delete) will be handled by the specific confirm functions
    if (dialogTitle === 'Confirm Approval') {
      await confirmApprove();
    } else if (dialogTitle === 'Confirm Delete') {
      await confirmDelete();
    }
  };

  const handleApprove = async () => {
    if (!entry || !user) return;
    
    setDialogTitle('Confirm Approval');
    setDialogMessage('Are you sure you want to approve this accounting entry?');
    setShowConfirmDialog(true);
  };

  const confirmApprove = async () => {
    if (!entry || !user) return;
    
    try {
      setApproving(true);
      const approvalData = {
        approved: true,
        approvedBy: user.id,
        approvedAt: new Date().toISOString(),
        comments: 'Approved by ' + user.name
      };
      
      const entryId = entry._id || entry.id;
      if (!entryId) {
        setDialogTitle('Error');
        setDialogMessage('Entry ID not found');
        setShowErrorDialog(true);
        return;
      }
      
      const response = await apiService.approveAccountingEntry(entryId, approvalData);
      
      if (response.success) {
        // Reload the entry to get updated data
        const updatedResponse = await apiService.getAccountingEntry(entryId);
        if (updatedResponse.success && updatedResponse.data) {
          setEntry(updatedResponse.data);
          
          // Fetch the approved by user details
          if (updatedResponse.data.approvedBy) {
            try {
              const approvedByResponse = await apiService.getUser(updatedResponse.data.approvedBy);
              if (approvedByResponse.success && approvedByResponse.data) {
                setApprovedByUser(approvedByResponse.data);
              }
            } catch (error) {
              console.error('Error loading approved by user:', error);
            }
          }
        }
        setDialogTitle('Success');
        setDialogMessage('Entry approved successfully!');
        setShowSuccessDialog(true);
      } else {
        setDialogTitle('Error');
        setDialogMessage('Failed to approve entry: ' + (response.message || 'Unknown error'));
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('Error approving entry:', error);
      setDialogTitle('Error');
      setDialogMessage('Failed to approve entry. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setApproving(false);
    }
  };

  return (
    <div>
      <BackButton as={Link} to="/office">
        &larr; Back to Office Finance
      </BackButton>
      
      <FlexContainer justify="space-between" style={{ marginBottom: '1rem' }}>
        <h1 className="page-title">Finance Entry Details</h1>
        {canManageFinances && (
          <FlexContainer gap="0.5rem">
            <OutlineButton as={Link} to={`/office/finance/edit/${entry._id || entry.id}`}>
              Edit Entry
            </OutlineButton>
            <Button 
              onClick={handleDelete}
              style={{ backgroundColor: 'var(--danger-color)' }}
            >
              Delete Entry
            </Button>
          </FlexContainer>
        )}
      </FlexContainer>
      
      <Grid columns="1fr 1fr" gap="1.5rem">
        <Card>
          <CardHeader>
            <h3>Entry Information</h3>
          </CardHeader>
          <CardBody>
            <FlexContainer direction="column" align="flex-start" gap="1.5rem">
              <DetailItem>
                <h4>ID</h4>
                <p>{entry._id || entry.id}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Description</h4>
                <p>{entry.description}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Amount</h4>
                <AmountText type={entry.type}>
                  {formatCurrency(entry.amount, entry.currency)}
                </AmountText>
              </DetailItem>
              
              <DetailItem>
                <h4>Type</h4>
                <Badge 
                  variant={
                    entry.type === 'Income' ? 'success' : 
                    entry.type === 'Expense' ? 'danger' : 
                    entry.type === 'Asset' ? 'info' : 
                    entry.type === 'Liability' ? 'warning' : 'secondary'
                  }
                >
                  {entry.type}
                </Badge>
              </DetailItem>
              
              <DetailItem>
                <h4>Category</h4>
                <p>{entry.category}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Account</h4>
                <p>{entry.account}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Date</h4>
                <p>{formatDate(entry.date)}</p>
              </DetailItem>
              
              {entry.reference && (
                <DetailItem>
                  <h4>Reference</h4>
                  <p>{entry.reference}</p>
                </DetailItem>
              )}
            </FlexContainer>
          </CardBody>
        </Card>
        
        <div>
          <Card>
            <CardHeader>
              <h3>Approval Information</h3>
            </CardHeader>
            <CardBody>
              <DetailItem>
                <h4>Status</h4>
                <ApprovalStatus color={entry.approved ? 'var(--success-color)' : 'var(--warning-color)'}>
                  <div className="status-dot"></div>
                  <span>{entry.approved ? 'Approved' : 'Pending Approval'}</span>
                </ApprovalStatus>
              </DetailItem>
              
              {entry.approved && (
                <>
                  <DetailItem>
                    <h4>Approved By</h4>
                    <p>{approvedByUser ? approvedByUser.name : (entry.approvedBy || 'N/A')}</p>
                  </DetailItem>
                  
                  <DetailItem>
                    <h4>Approved At</h4>
                    <p>{entry.approvedAt ? formatDate(entry.approvedAt) : 'N/A'}</p>
                  </DetailItem>
                </>
              )}
              
              <DetailItem>
                <h4>Created By</h4>
                <p>{createdByUser ? createdByUser.name : (entry.createdBy || 'N/A')}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Created At</h4>
                <p>{formatDate(entry.createdAt)}</p>
              </DetailItem>
              
              {!entry.approved && canManageFinances && (user?.role === 'MD' || user?.role === 'TD') && (
                <Button 
                  style={{ marginTop: '1rem' }}
                  onClick={handleApprove}
                  disabled={approving}
                >
                  {approving ? 'Approving...' : 'Approve Entry'}
                </Button>
              )}
            </CardBody>
          </Card>
          
          <Card style={{ marginTop: '1.5rem' }}>
            <CardHeader>
              <h3>Attachments</h3>
              {canManageFinances && (
                <SmallOutlineButton>
                  + Add Attachment
                </SmallOutlineButton>
              )}
            </CardHeader>
            <CardBody>
              {entry.attachments && entry.attachments.length > 0 ? (
                <AttachmentList>
                  {entry.attachments.map((attachment: Document, index: number) => (
                    <Attachment key={index}>
                      <div className="file-icon">
                        {attachment.type.split('/')[1]?.toUpperCase() || 'FILE'}
                      </div>
                      <div className="file-info">
                        <div className="file-name">{attachment.name}</div>
                        <div className="file-meta">
                          {(attachment.size / 1024).toFixed(2)} KB • Uploaded {formatDate(attachment.uploadedAt)}
                        </div>
                      </div>
                      <SmallOutlineButton>
                        View
                      </SmallOutlineButton>
                    </Attachment>
                  ))}
                </AttachmentList>
              ) : (
                <NoAttachments>No attachments available</NoAttachments>
              )}
            </CardBody>
          </Card>
        </div>
      </Grid>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
      
      {/* Custom Dialogs */}
      <CustomDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title={dialogTitle}
        message={dialogMessage}
        type="confirm"
        onConfirm={handleConfirmDialog}
        confirmText="Confirm"
        cancelText="Cancel"
        showCancel={true}
      />
      
      <CustomDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={dialogTitle}
        message={dialogMessage}
        type="success"
        confirmText="OK"
        showCancel={false}
      />
      
      <CustomDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={dialogTitle}
        message={dialogMessage}
        type="error"
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
};

export default FinanceDetail; 